import {
  ActivityIcon,
  ChartColumnBig,
  HandshakeIcon,
  HomeIcon,
  ScaleIcon,
  UsersIcon,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { useRouteTransition } from '@/hooks/useRouteTransition';

import { cn } from '@/lib/utils';

interface Tab {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  testId?: string;
}

interface GroupTabsProps {
  groupId: string;
  className?: string;
  onTabClick?: (href: string) => void;
  pendingSettlementsCount?: number;
}

export function GroupTabs({
  groupId,
  className,
  onTabClick,
  pendingSettlementsCount,
}: GroupTabsProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { t } = useTranslation('groups');
  const { startTransition } = useRouteTransition();

  const tabs: Tab[] = [
    {
      name: t('groups:tabs.overview'),
      href: `/groups/${groupId}`,
      icon: HomeIcon,
      testId: 'group-tab-dashboard',
    },
    // Members sits right after Overview so inviting people is easy to find.
    {
      name: t('groups:tabs.members'),
      href: `/groups/${groupId}/members`,
      icon: UsersIcon,
      testId: 'group-tab-members',
    },
    {
      name: t('groups:tabs.balances'),
      href: `/groups/${groupId}/balances`,
      icon: ScaleIcon,
      testId: 'group-tab-balances',
    },
    {
      name: t('groups:tabs.settlements'),
      href: `/groups/${groupId}/settlements`,
      icon: HandshakeIcon,
      testId: 'group-tab-settlements',
      badge: pendingSettlementsCount || undefined,
    },
    {
      name: t('groups:tabs.reports'),
      href: `/groups/${groupId}/reports`,
      icon: ChartColumnBig,
      testId: 'group-tab-reports',
    },
    {
      name: t('groups:tabs.activity'),
      href: `/groups/${groupId}/activity`,
      icon: ActivityIcon,
      testId: 'group-tab-activity',
    },
  ];

  const isActive = (href: string) => {
    if (href === `/groups/${groupId}`) {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  return (
    <div
      className={cn('border-b border-gray-200 dark:border-gray-700', className)}
    >
      <nav
        className="-mb-px flex overflow-x-auto overflow-y-hidden scrollbar-hide"
        aria-label="Tabs"
      >
        {tabs.map(tab => {
          const active = isActive(tab.href);
          const Icon = tab.icon;

          return (
            <Link
              key={tab.name}
              to={tab.href}
              data-testid={tab.testId}
              onClick={e => {
                e.preventDefault();
                // Start transition immediately for instant feedback
                startTransition(tab.href);
                if (onTabClick) {
                  onTabClick(tab.href);
                }
                // Navigate immediately
                navigate(tab.href);
              }}
              className={cn(
                'group flex-1 min-w-0 flex justify-center items-end pt-2 px-0 sm:px-3 font-medium text-xs sm:text-sm -mb-px',
                active
                  ? 'text-orange-500 dark:text-orange-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              )}
            >
              <div
                className={cn(
                  'inline-flex flex-col items-center gap-0.5 whitespace-nowrap border-b-[3px] pb-2',
                  active
                    ? 'border-orange-500 dark:border-orange-400'
                    : 'border-transparent group-hover:border-gray-300'
                )}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      active
                        ? 'text-orange-500 dark:text-orange-400'
                        : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                    )}
                  />
                  {tab.badge && tab.badge > 0 && (
                    <span className="absolute -top-2 -right-5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </div>
                <span className="text-[11px] sm:text-xs">{tab.name}</span>
              </div>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
